import React, { useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import debounce from 'lodash/debounce'
import { usePath, useStoreConfig } from 'lib/core/hooks'
import { useSearchSuggestions, useSearchTerms, createHandleSearchTermRouting } from 'lib/modules/misc'

const DELAY = 500
const MIN_LENGTH = 3
const PAGE_SIZE = 5

interface Params {
  delay?: number
  minLength?: number
  pageSize?: number
  withSuggestions?: boolean
}

export const useSearchBox = (params?: Params) => {
  const delay = params?.delay ?? DELAY
  const minLength = params?.delay ?? MIN_LENGTH
  const pageSize = params?.pageSize ?? PAGE_SIZE
  const withSuggestions = params?.withSuggestions ?? false

  const history = useHistory()
  const { search: searchPath } = usePath()
  const { pwaUrl } = useStoreConfig()
  const [submitted, setSubmitted] = useState(false)
  const [value, setValue] = useState('')
  const [query, setQuery] = useState('')
  const [hasFocus, setHasFocus] = useState(false)
  const delayedQuery = useRef(debounce(setQuery, delay)).current

  const { products, hasItems, totalCount } = useSearchSuggestions({
    minLength,
    pageSize,
    search: query,
    enable: withSuggestions
  })

  const isOnSearch = history.location.pathname.includes(searchPath.replace(':query', value))
  const trimmedValue = value.trim()
  const showSuggestions = !submitted && hasItems && hasFocus && trimmedValue.length >= minLength && !isOnSearch

  const handleSearchTermRouting = createHandleSearchTermRouting({
    value: trimmedValue,
    pwaUrl,
    searchPath,
    history
  })

  const resetInputState = () => {
    setSubmitted(false)
    setHasFocus(false)
    setValue('')
    setQuery('')
  }

  const { searchTerm, isFetching: searchTermFetching } = useSearchTerms({
    search: value,
    onSuccess: ({ searchTerm }) => {
      if (!submitted) {
        return
      }

      resetInputState()
      handleSearchTermRouting(searchTerm)
    }
  })

  const isAwaiting = submitted && searchTermFetching

  const goToSearchPage = () => {
    if (searchTermFetching) {
      setSubmitted(true)
    } else {
      handleSearchTermRouting(searchTerm)
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (trimmedValue) {
      goToSearchPage()
    }
    e.preventDefault()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isAwaiting) {
      return
    }

    const value = event.target.value
    setSubmitted(false)
    setValue(value)
    delayedQuery(value)
  }

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select()
    setHasFocus(true)
  }

  const handleInputBlur = () => {
    setTimeout(resetInputState, 300)
  }

  return {
    handleFormSubmit,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    goToSearchPage,
    showSuggestions,
    hasFocus,
    isAwaiting,
    value,
    totalCount,
    products: hasItems && products ? products : []
  }
}
